import { useState } from "react";
import {
  DollarOutlined,
  ShoppingOutlined,
  ExclamationOutlined,
  TeamOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Card, Col, Row, Statistic, Typography, Timeline, DatePicker } from "antd";
import {
  Container,
  GraphContainer,
  LineCard,
  RankingCard,
  Gradient,
  InformationCard,
  InformationFooter,
  WhiteParagraph,
  LinkRight,
} from "./elements";
import LineChart from "./components/LineChart";
import { Link } from "react-router-dom";
import { GET_ORDERS, GET_CLIENTS, GET_ORDERS_DAILY_SUMMARY } from "./requests";
import { useQuery } from "@apollo/client";
import moment from "moment-timezone";
import { getFullWeekData, formatCurrency } from "../../utils/functions";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;

// A reusable style for all cards
const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 3px 6px rgba(0, 0, 0, 0.05)",
};

export default function Dashboard() {
  const today = moment().tz("America/Mexico_City").startOf("day");
  const weekStart = today.clone().startOf("week");
  const weekEnd = today.clone().endOf("week");

  const [customStart, setCustomStart] = useState<moment.Moment | null>(null);
  const [customEnd, setCustomEnd] = useState<moment.Moment | null>(null);

  const effectiveStart = customStart || weekStart;
  const effectiveEnd = customEnd || weekEnd;

  const { data: ordersSummary, loading: ordersSummaryLoading } = useQuery(GET_ORDERS_DAILY_SUMMARY, {
    variables: { startDate: effectiveStart.clone().startOf("day").format(),
      endDate: effectiveEnd.clone().endOf("day").format() },
  });

  const { data: ordersDailyData, loading: ordersDailyLoading } = useQuery(GET_ORDERS, {
    variables: { startDate: (customStart || today).format(),
      endDate: (customEnd || today).format(),
      params: { pageSize: 1000,
        page: 1 } },
  });

  const { data: newClients, loading: newClientsLoading } = useQuery(
    GET_CLIENTS,
    {
      variables: {
        params: { pageSize: 15,
          page: 1 },
        startDate: (customStart || today).format(),
        endDate: (customEnd || today).format(),
      },
    },
  );

  const { data: clients, loading: clientsLoading } = useQuery(GET_CLIENTS, {
    variables: { params: { pageSize: 15,
      page: 1 } },
  });

  const rankingClients = clients?.clients?.results.map((client: any) => ({
    id: client.id,
    name: `${client.user.firstName} ${client.user.lastName}`,
    totalSpent: client.moneySpent,
  }));

  const sortedClients = rankingClients?.sort((a: any, b: any) => b.totalSpent - a.totalSpent);

  const { categories, orders, revenue } = getFullWeekData(
    effectiveStart,
    effectiveEnd,
    ordersSummary?.ordersDailySummary || [],
  );

  return (
    <>
      <Container>
        <Row>
          <Col span={17}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card
                  loading={ordersDailyLoading || newClientsLoading}
                  style={{ ...cardStyle }}
                >
                  <Statistic
                    title="Órdenes Hoy"
                    value={ordersDailyData?.orders.info.count}
                    prefix={<ShoppingOutlined style={{ color: "#e67e22" }} />}
                    suffix="🔥"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card
                  loading={ordersDailyLoading || newClientsLoading}
                  style={{ ...cardStyle }}
                >
                  <Statistic
                    title="Ordenes Pendientes"
                    value={ordersDailyData?.orders.results.reduce(
                      (acc: number, order: any) => {
                        return order.status !== "complete" ? acc + 1 : acc;
                      },
                      0,
                    )}
                    prefix={
                      <ExclamationOutlined style={{ color: "#f1c40f" }} />
                    }
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card
                  loading={ordersDailyLoading || newClientsLoading}
                  style={{ ...cardStyle }}
                >
                  <Statistic
                    title="Clientes Nuevos"
                    value={newClients?.clients?.info?.count}
                    prefix={<TeamOutlined style={{ color: "#3498db" }} />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card
                  loading={ordersDailyLoading || newClientsLoading}
                  style={{ ...cardStyle }}
                >
                  <Statistic
                    title="Ingresos Diarios"
                    value={
                      ordersDailyData?.orders.results
                        ? ordersDailyData.orders.results
                          .reduce((acc: number, order: any) => {
                            return (
                              acc +
                                (typeof order.price === "number"
                                  ? order.price
                                  : 0)
                            );
                          }, 0)
                          .toFixed(2)
                        : 0
                    }
                    precision={2}
                    prefix={<DollarOutlined style={{ color: "#2ecc71" }} />}
                  />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 16,
              marginLeft: 20 }}>
              <Col>
                <Text>De: </Text>
                <DatePicker
                  value={customStart ? dayjs(customStart.format()) : null}
                  onChange={date => setCustomStart(date ? moment(date.format()) : null)}
                  disabledDate={current => current && current.isAfter(dayjs())}
                />
              </Col>
              <Col>
                <Text>Hasta: </Text>
                <DatePicker
                  value={customEnd ? dayjs(customEnd.format()) : null}
                  onChange={date => setCustomEnd(date ? moment(date.format()) : null)}
                  disabledDate={current => current && current.isAfter(dayjs())}
                />
              </Col>
            </Row>
            <GraphContainer>
              <LineCard
                loading={ordersSummaryLoading}
                style={{
                  borderRadius: 20,
                  boxShadow: "5px 5px 5px lightblue",
                }}
              >
                <LineChart
                  loading={ordersSummaryLoading}
                  ordersData={[
                    { name: "Órdenes",
                      data: orders },
                    { name: "Ingresos",
                      data: revenue },
                  ]}
                  categories={categories}
                />
              </LineCard>
            </GraphContainer>
          </Col>

          <Col span={7} style={{ justifyContent: "space-between" }}>
            <RankingCard
              loading={clientsLoading}
              style={{
                borderRadius: 20,
                boxShadow: "5px 5px 5px lightblue",
              }}
            >
              <Col>
                <Row
                  style={{
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      Clientes
                    </Text>
                    <Paragraph style={{ color: "#8c8c8c" }}>
                      Ranking Clientes
                    </Paragraph>
                  </div>
                </Row>
              </Col>
              <Timeline
                style={{
                  overflow: "auto",
                  height: "35vh",
                }}
              >
                {sortedClients?.map((client, index) => {
                  return (
                    <Timeline.Item color="blue" key={index}>
                      <Row
                        style={{
                          backgroundColor: "white",
                          borderRadius: "8px",
                          transition: "background-color 0.2s ease-in-out",
                          display: "flex",
                          alignItems: "center",
                          marginTop: 10,
                        }}
                      >
                        <div style={{ marginLeft: 10 }}>
                          <Text
                            style={{ fontSize: 15 }}
                            strong
                          >{client.name}</Text>
                          <br />
                          <Text>
                            Total:{" "}
                            {formatCurrency(client.totalSpent.toFixed(2))}
                          </Text>
                        </div>
                      </Row>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </RankingCard>
            <Gradient style={{ borderRadius: 12 }}>
              <InformationCard>
                <div>
                  <Title
                    level={5}
                    style={{ color: "white",
                      fontWeight: "800" }}
                  >
                    Productos
                  </Title>
                  <WhiteParagraph style={{ color: "white" }}>
                    Revisa los Productos para esta sucursal para llevar un
                    control.
                  </WhiteParagraph>
                </div>
                <InformationFooter>
                  <Row style={{ justifyContent: "space-between" }}>
                    <Link to="/products">
                      <LinkRight style={{ color: "white" }}>
                        Productos
                        <RightOutlined style={{ color: "white" }} />
                      </LinkRight>
                    </Link>
                  </Row>
                </InformationFooter>
              </InformationCard>
            </Gradient>
          </Col>
        </Row>
      </Container>
    </>
  );
}
